import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button,
  Grid, Snackbar, Alert, CircularProgress,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import BusinessIcon from '@mui/icons-material/Business';
import api from '../../services/api';
import useAuth from '../../hooks/useAuth';

const DirectorSettings = () => {
  const { user, getUserIsta } = useAuth();
  const isAdmin = user?.role === 'admin';
  const ista = getUserIsta();

  const [settings, setSettings] = useState({
    dr: '', centre_formation: '', annee_formation: '', etablissement: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      // RH users use ISTA-specific settings from their ista
      if (!isAdmin && ista) {
        setSettings({
          dr: ista.dr || '',
          centre_formation: ista.centre_formation || '',
          annee_formation: ista.annee_formation || '',
          etablissement: ista.nom || '',
        });
      } else {
        // Admin: load global settings
        const response = await api.get('/settings');
        setSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      showSnackbar('Erreur lors du chargement des paramètres', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put('/director/settings', { settings });
      showSnackbar('Paramètres enregistrés avec succès', 'success');
    } catch (error) {
      console.error('Error updating settings:', error);
      showSnackbar("Erreur lors de l'enregistrement", 'error');
    } finally {
      setSaving(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <BusinessIcon color="primary" sx={{ fontSize: 32 }} />
        <Typography variant="h4" fontWeight={700}>
          {isAdmin ? 'Paramètres Globaux' : `Paramètres — ${ista?.nom || 'ISTA'}`}
        </Typography>
      </Box>

      {!isAdmin && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Ces paramètres sont spécifiques à votre ISTA (<strong>{ista?.nom}</strong>). 
          Ils seront utilisés dans la génération des documents (fiches de candidature, contrats, etc.).
        </Alert>
      )}

      <Card sx={{ maxWidth: 800, mt: 2, boxShadow: 3 }}>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth label="Direction Régionale (DR)"
                  name="dr" value={settings.dr || ''}
                  onChange={handleChange} variant="outlined" required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth label="Complexe"
                  name="centre_formation" value={settings.centre_formation || ''}
                  onChange={handleChange} variant="outlined" required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth label="Année de formation"
                  name="annee_formation" value={settings.annee_formation || ''}
                  onChange={handleChange} variant="outlined" required
                  placeholder="ex: 2025-2026"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth label="Établissement"
                  name="etablissement" value={settings.etablissement || ''}
                  onChange={handleChange} variant="outlined" required
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  type="submit" variant="contained" color="primary" size="large"
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                >
                  {saving ? 'Enregistrement...' : 'Enregistrer les paramètres'}
                </Button>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open} autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DirectorSettings;
